import React, { Children, lazy } from 'react';
import { motion } from 'framer-motion';
import { Trophy, Star, Globe, Medal, Play } from 'lucide-react';
import { useLanguage } from './LanguageContext';
export function TeamOverview() {
  const {
    t
  } = useLanguage();
  const achievements = [{
    icon: <Globe size={32} className="text-germany-gold mb-4" data-id="element-322" />,
    title: t('team.wc'),
    desc: t('team.wcDesc')
  }, {
    icon: <Trophy size={32} className="text-germany-gold mb-4" data-id="element-323" />,
    title: t('team.euro'),
    desc: t('team.euroDesc')
  }, {
    icon: <Medal size={32} className="text-germany-gold mb-4" data-id="element-324" />,
    title: t('team.confed'),
    desc: t('team.confedDesc')
  }, {
    icon: <Star size={32} className="text-germany-gold mb-4" data-id="element-325" />,
    title: t('team.appearances'),
    desc: t('team.appearancesDesc')
  }];
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6
      }
    }
  };
  return <>
      <section id="team" className="py-12 md:py-24 bg-germany-black relative" data-id="element-326">
        {/* Decorative background element */}
        <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-germany-platinum/30 to-transparent" data-id="element-327"></div>
        <div className="container mx-auto px-6 md:px-12" data-id="element-328">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center" data-id="element-329">
            {/* Text Content */}
            <motion.div initial={{
            opacity: 0,
            x: -30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true,
            margin: '-100px'
          }} transition={{
            duration: 0.8
          }} data-id="element-330">
              <h2 className="text-germany-red font-oswald uppercase tracking-widest text-sm font-bold mb-3" data-id="element-331">
                {t('team.label')}
              </h2>
              <h3 className="text-4xl md:text-5xl font-oswald font-bold uppercase leading-tight mb-6" data-id="element-332">
                {t('team.title1')} <br data-id="element-333" />
                <span className="text-germany-gold" data-id="element-334">{t('team.title2')}</span>
              </h3>
              <p className="text-gray-400 text-lg leading-relaxed mb-8" data-id="element-335">
                {t('team.desc')}
              </p>
              <div className="flex items-center gap-4 mb-8" data-id="element-336">
                <div className="w-12 h-1 bg-germany-platinum" data-id="element-337"></div>
                <span className="font-oswald uppercase tracking-widest text-sm text-germany-platinum" data-id="element-338">
                  {t('team.since')}
                </span>
              </div>
              <a href="https://en.wikipedia.org/wiki/Germany_national_football_team" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 border border-germany-platinum/30 text-white px-6 py-3 rounded-full font-oswald uppercase tracking-wider text-sm font-bold transition-all duration-300 min-h-[44px]" data-id="element-339">
                {t('team.history')}
              </a>
            </motion.div>
            {/* Achievement Grid */}
            <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
            once: true,
            margin: '-50px'
          }} className="grid grid-cols-2 gap-4 md:gap-6" data-id="element-340">
              {achievements.map((item, index) => <motion.div key={index} variants={itemVariants} className="bg-[#111] border border-germany-platinum/10 p-6 rounded-2xl hover:border-germany-platinum/40 transition-colors duration-300 group relative overflow-hidden" data-id="element-341">
                  <div className="absolute top-0 left-0 w-full h-[2px] bg-gradient-to-r from-germany-platinum/0 via-germany-platinum/40 to-germany-platinum/0" data-id="element-342"></div>
                  <div className="transform group-hover:scale-110 transition-transform duration-300 origin-left" data-id="element-343">
                    {item.icon}
                  </div>
                  <h4 className="font-oswald text-xl font-bold uppercase mb-1" data-id="element-344">
                    {item.title}
                  </h4>
                  <p className="text-sm text-gray-400" data-id="element-345">{item.desc}</p>
                </motion.div>)}
            </motion.div>
          </div>
        </div>
      </section>
      {/* Video Embed */}
      <section className="py-10 md:py-16 bg-[#111] relative" data-id="element-346">
        <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-germany-platinum/20 to-transparent" data-id="element-347"></div>
        <div className="container mx-auto px-4 md:px-12" data-id="element-348">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-center mb-6 md:mb-8" data-id="element-349">
            <h2 className="text-germany-red font-oswald uppercase tracking-widest text-xs md:text-sm font-bold mb-2" data-id="element-350">
              {t('team.videoLabel')}
            </h2>
            <h3 className="text-2xl md:text-4xl font-oswald font-bold uppercase" data-id="element-351">
              {t('team.videoTitle1')}{' '}
              <span className="text-germany-gold" data-id="element-352">{t('team.videoTitle2')}</span>
            </h3>
          </motion.div>
          <motion.div initial={{
          opacity: 0,
          scale: 0.95
        }} whileInView={{
          opacity: 1,
          scale: 1
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} className="relative w-full max-w-4xl mx-auto rounded-2xl overflow-hidden border border-white/10 shadow-2xl shadow-germany-dark-red/20" data-id="element-353">
            <div className="relative w-full" style={{
            paddingBottom: '56.25%'
          }} data-id="element-354">
              {<a href="https://www.youtube.com/watch?v=El4rO-rrPuo" target="_blank" rel="noopener noreferrer" className="absolute inset-0 w-full h-full group cursor-pointer bg-black block" aria-label="Play video on YouTube" data-id="element-355">
                  <img src="https://img.youtube.com/vi/El4rO-rrPuo/maxresdefault.jpg" alt="Germany Video" loading="lazy" decoding="async" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" data-id="element-356" />
                  <div className="absolute inset-0 bg-black/40 group-hover:bg-black/50 transition-colors duration-300" data-id="element-357" />
                  <div className="absolute inset-0 flex items-center justify-center" data-id="element-358">
                    <div className="w-16 h-16 md:w-20 md:h-20 bg-germany-red/90 rounded-full flex items-center justify-center backdrop-blur-sm shadow-[0_0_30px_rgba(221,0,0,0.5)] group-hover:scale-110 transition-transform duration-300" data-id="element-359">
                      <Play className="text-white ml-1" size={28} fill="currentColor" data-id="element-360" />
                    </div>
                  </div>
                  <div className="absolute top-4 right-4 bg-[#FF0000] text-white text-[10px] font-oswald uppercase tracking-widest px-2 py-1 rounded flex items-center gap-1" data-id="element-361">
                    <Play size={10} fill="currentColor" data-id="element-362" /> YouTube
                  </div>
                </a>}
            </div>
          </motion.div>
        </div>
      </section>
    </>;
}